import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { DeleteModal, ProfileDetails } from "../v3";
import { PurchaseService } from "../../services";
import { DELETE_SUBCONTRACTOR, EDIT_SUBCONTRACTOR } from "../../constants";
import { AddEditSubContractor } from "./AddEditSubcontractor.component";
import { formatBalance, getBalanceColor, useHasPermission } from "../../utils";
import { SUBCONTRACTOR_AVATAR } from "../../utils/image.constants";

export const SubcontractorDetails = ({ onSuccess = () => {}, reloadData }) => {
  const [subcontractorDetails, setSubcontractorDetails] = useState({});
  const [isActive, setIsActive] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    subcontractorId: null,
  });

  const { id } = useParams();
  const navigate = useNavigate();

  const hadSubcontractorEdit = useHasPermission(EDIT_SUBCONTRACTOR);
  const hadSubcontractorDelete = useHasPermission(DELETE_SUBCONTRACTOR);

  useEffect(() => {
    fetchData(id);
  }, [id, reloadData]);

  const fetchData = async (subcontractorId) => {
    try {
      const { data } = await PurchaseService.getVendorById(subcontractorId);
      setSubcontractorDetails(data);
      setIsActive(data?.active);
      onSuccess?.(data?.name);
    } catch (error) {
      toast.error("Failed to fetch contractor details");
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const openDeleteModal = () => {
    setDeleteModal({
      isOpen: true,
      subcontractorId: id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      subcontractorId: null,
    });
  };

  const handleDelete = async () => {
    try {
      await PurchaseService.deleteVendor(deleteModal.subcontractorId);
      toast("Subcontractor deleted successfully", {
        type: "success",
      });
      closeDeleteModal();
      navigate(-1);
    } catch (error) {
      toast("Failed to delete subcontractor", {
        type: "error",
      });
      console.error("🚀 ~ deleteEntity ~ error:", error);
    }
  };

  const handleStatusChange = async (e) => {
    const updatedStatus = e.target.checked;
    try {
      await PurchaseService.updateVendor(id, { active: updatedStatus });
      setIsActive(updatedStatus);
      toast.success("Status updated successfully");
    } catch (error) {
      toast.error("Failed to update status");
      console.error("🚀 ~ onActiveClick ~ error:", error);
    }
  };

  return (
    <>
      <ProfileDetails
        profileImage={SUBCONTRACTOR_AVATAR}
        name={subcontractorDetails?.name}
        phone={subcontractorDetails?.primaryPhone ? subcontractorDetails.primaryPhone : "not available"}
        isActive={isActive}
        canToggle={hadSubcontractorEdit}
        canEdit={hadSubcontractorEdit}
        canDelete={hadSubcontractorDelete}
        onEdit={handleOpenModal}
        onDelete={openDeleteModal}
        onToggle={(e) => handleStatusChange(e)}
        infoFields={[
          {
            label: "Balance",
            value: formatBalance(subcontractorDetails?.account?.balance),
            color: getBalanceColor(subcontractorDetails?.account?.balance),
          },
          {
            label: "Place",
            value: subcontractorDetails?.primaryAddress?.locality || "-",
          },
        ]}
      />

      {/* Edit Subcontractor Modal */}
      <AddEditSubContractor contractorId={id} open={openModal} onClose={handleCloseModal} onSuccess={() => fetchData(id)} />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this subcontractor?"
      />
    </>
  );
};
